import { router } from "@inertiajs/react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Search, X } from "lucide-react"
import type { Filters, TagOption } from "@/types"

type Props = {
  filters: Filters
  availableKeys: string[]
  popularTags: TagOption[]
}

export function SampleFilters({ filters, availableKeys, popularTags }: Props) {
  const updateFilters = (newFilters: Partial<Filters>) => {
    const merged = { ...filters, ...newFilters }
    const params: Record<string, string | string[]> = {}

    if (merged.q) params.q = merged.q
    if (merged.bpm_min) params.bpm_min = merged.bpm_min
    if (merged.bpm_max) params.bpm_max = merged.bpm_max
    if (merged.key) params.key = merged.key
    if (merged.tag_ids?.length) params.tag_ids = merged.tag_ids.map(String)
    if (merged.sort !== "recent") params.sort = merged.sort

    router.get("/samples", params, { preserveState: true, preserveScroll: true })
  }

  const clearFilters = () => {
    router.get("/samples", {}, { preserveState: true })
  }

  const hasFilters = !!(filters.q || filters.bpm_min || filters.bpm_max || filters.key || filters.tag_ids?.length)

  return (
    <div className="space-y-4 mb-6">
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search samples..."
            defaultValue={filters.q || ""}
            className="pl-9 font-mono border-muted-foreground/30"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                updateFilters({ q: e.currentTarget.value })
              }
            }}
          />
        </div>

        {/* BPM Range */}
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="Min BPM"
            defaultValue={filters.bpm_min || ""}
            className="w-28 font-mono border-muted-foreground/30"
            onBlur={(e) => updateFilters({ bpm_min: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && updateFilters({ bpm_min: e.currentTarget.value })}
          />
          <span className="text-muted-foreground">-</span>
          <Input
            type="number"
            placeholder="Max"
            defaultValue={filters.bpm_max || ""}
            className="w-20 font-mono border-muted-foreground/30"
            onBlur={(e) => updateFilters({ bpm_max: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && updateFilters({ bpm_max: e.currentTarget.value })}
          />
        </div>

        {/* Key Select */}
        <Select
          value={filters.key || "all"}
          onValueChange={(value) => updateFilters({ key: value === "all" ? undefined : value })}
        >
          <SelectTrigger className="w-32 font-mono border-muted-foreground/30">
            <SelectValue placeholder="Key" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Keys</SelectItem>
            {availableKeys.map((key) => (
              <SelectItem key={key} value={key}>{key}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select
          value={filters.sort}
          onValueChange={(value) => updateFilters({ sort: value as Filters["sort"] })}
        >
          <SelectTrigger className="w-32 border-muted-foreground/30">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Recent</SelectItem>
            <SelectItem value="popular">Popular</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="icon" onClick={clearFilters}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Tags */}
      {popularTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {popularTags.map((tag) => {
            const isSelected = filters.tag_ids?.includes(tag.id)
            return (
              <Button
                key={tag.id}
                variant={isSelected ? "default" : "outline"}
                size="sm"
                className="text-xs border-muted-foreground/40 border-flicker-hover hover:text-primary!"
                onClick={() => {
                  const current = filters.tag_ids || []
                  const newTags = isSelected
                    ? current.filter((id) => id !== tag.id)
                    : [...current, tag.id]
                  updateFilters({ tag_ids: newTags })
                }}
              >
                {tag.name}
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}
